import { computed } from "mobx";
import { Router } from "@frui.ts/screens";
import type ScreenBase from "@frui.ts/screens/dist/structure/screenBase";
import { bound } from "@frui.ts/helpers";
import type { PropertyName } from "@frui.ts/helpers";
import { Modules } from "config";
import LocalizationService from "services/localizationService";
import EventBus from "services/eventBus";
import SecurityService from "services/securityService";
import NotificationService from "services/notificationService";
import OptionsService from "services/optionsService";
import DeadLetterTopicMessageResponse from "entities/deadLetterTopicMessageResponse";
import type kafkaDltMessageType from "entities/kafkaDltMessageType";
import type kafkaDltTopic from "entities/kafkaDltTopic";
import DeadLetterTopicMessagesSearchRequest from "entities/deadLetterTopicMessagesSearchRequest";
import KafkaDltMessageType from "entities/kafkaDltMessageType";
import DeadLetterTopicsRepository from "repositories/deadLetterTopicsRepository";
import type PagingManagerViewModel from "viewModels/pagingManagerViewModel";
import ListViewModel from "viewModels/listViewModel";
import Selectable, * as selectableListViewModel from "viewModels/selectableListViewModel";
import type DeadLetterTopicsSearchDetailViewModel from "./deadLetterTopicsSearchDetailViewModel";

const navigationName = "dead-letter-topics-search";

@Router.registerRoute({
  name: Router.Self,
  route: navigationName,
})
@Selectable
export default class DeadLetterTopicsSearchListViewModel extends ListViewModel<DeadLetterTopicMessageResponse, ScreenBase> {
  readonly modelName = "deadLetterTopicsSearch";

  @computed
  get messageTypeOptions() {
    return this.optionsService.internalEnumOptions(KafkaDltMessageType);
  }

  @computed
  get self(): selectableListViewModel.SelectableListViewModel<
    DeadLetterTopicsSearchListViewModel,
    DeadLetterTopicMessageResponse
  > {
    return this as any;
  }

  @computed
  get name() {
    if (!this.isInitialized) {
      return this.translate("name");
    } else {
      return this.translate("nameCount").replace("%count%", this.currentPaging?.totalItems.toString() || "0");
    }
  }

  constructor(
    public repository: DeadLetterTopicsRepository,
    public securityService: SecurityService,
    public eventBus: EventBus,
    public localizationService: LocalizationService,
    protected detailViewModelFactory: ReturnType<typeof DeadLetterTopicsSearchDetailViewModel.Factory>,
    public notificationService: NotificationService,
    private optionsService: OptionsService,
    private pagingManagerFactory: ReturnType<typeof PagingManagerViewModel.Factory>
  ) {
    super();
    this.navigationName = navigationName;
  }

  protected createFilter(): DeadLetterTopicMessagesSearchRequest {
    return new DeadLetterTopicMessagesSearchRequest();
  }

  protected detailParams(itemId: string) {
    const pagingManager = this.pagingManagerFactory(this, itemId);
    return [pagingManager];
  }

  @bound
  allowItemDelete(item: DeadLetterTopicMessageResponse) {
    return false;
  }

  @bound
  allowItemDetail(item: any) {
    return false;
  }

  translateFilterAttribute(attribute: PropertyName<DeadLetterTopicMessagesSearchRequest>, size?: number) {
    return this.localizationService.translateAttribute("deadLetterTopicMessagesSearchRequest", attribute, size);
  }

  translateTypeState(state: kafkaDltMessageType) {
    return this.localizationService.translateEnum("kafkaDltMessageType", state);
  }

  translateTopicState(state: kafkaDltTopic) {
    return this.localizationService.translateEnum("kafkaDltTopic", state);
  }

  translate(code: string, size?: number): string {
    return this.localizationService.translateModule(Modules.administration, `deadLetterTopicsSearch.${code}`, size);
  }
}
